import { useEffect, useState } from "react";
import Dropzone from "react-dropzone";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import CustomButton from "../../../ui/button/Button";
import {
  validarExcelTrabajos,
  aplicarExcelTrabajos,
} from "../../../components/functions/Projects";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

const ModalSubirExcelAsignaciones = ({
  API_URL,
  open,
  onClose,
  setShowLoader,
}) => {
  const [file, setFile] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [tableResponseData, setTableResponseData] = useState([]);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (open) {
      setFile(null);
      setTableData([]);
      setTableResponseData([]);
    }
  }, [open]);

  useEffect(() => {
    const handleFileUpload = async () => {
      try {
        setShowLoader(true);
        const res = await validarExcelTrabajos(API_URL, file);
        setTableData(res);
        setShowLoader(false);
      } catch (error) {
        setShowLoader(false);
        setShowSnackbar(true);
        setSnackbarMessage("Ocurrió un error al subir el archivo");
      }
    };
    if (file) {
      handleFileUpload();
    }
  }, [file]);

  const handleClose = () => {
    onClose();
  };

  const limpiarNoValidos = () => {
    setTableData((prev) => prev.filter((row) => row.esValida === true));
  };

  const handleAplicarAsignaciones = async () => {
    setShowConfirm(false);
    const payload = tableData.filter((row) => row.esValida === true);
    if (payload.length === 0) {
      setShowSnackbar(true);
      setSnackbarMessage("No hay asignaciones válidas para aplicar");
      return;
    }
    try {
      setShowLoader(true);
      const res = await aplicarExcelTrabajos(API_URL, payload);
      setTableResponseData(res);
      setShowLoader(false);
      setShowSnackbar(true);
      setSnackbarMessage("Asignaciones aplicadas correctamente");
    } catch (error) {
      setShowLoader(false);
      setShowSnackbar(true);
      setSnackbarMessage("Ocurrió un error al aplicar las asignaciones");
    }
  };

  return (
    <>
      <Dialog
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Confirmación</DialogTitle>
        <DialogContent>
          ¿Estás seguro de aplicar las asignaciones?
        </DialogContent>
        <DialogActions>
          <CustomButton
            background={"#FFFFFF"}
            color={"#223D51"}
            text="Cancelar"
            height="40px"
            fontWeight="700"
            fontSize="14px"
            border="1px solid #D9D9D9"
            type="button"
            width={"100px"}
            onClick={() => setShowConfirm(false)}
          />
          <CustomButton
            background={"#223D51"}
            color={"#FFFFFF"}
            text="Aceptar"
            height="40px"
            fontWeight="700"
            fontSize="14px"
            type="button"
            width={"100px"}
            onClick={handleAplicarAsignaciones}
          />
        </DialogActions>
      </Dialog>
      <Dialog fullWidth maxWidth="lg" open={open} onClose={handleClose}>
        <Snackbar
          open={showSnackbar}
          autoHideDuration={3000}
          onClose={() => {
            setShowSnackbar(false);
            setSnackbarMessage("");
          }}
          message={snackbarMessage}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          slotProps={{
            content: {
              sx: {
                backgroundColor: "white",
                color: "#222",
                justifyContent: "center",
                textAlign: "center",
                fontWeight: "bold",
                fontSize: "16px",
                fontFamily: "Roboto, Arial, sans-serif",
              },
            },
          }}
        />
        <DialogTitle>Validar y aplicar asignaciones con excel</DialogTitle>
        <DialogContent>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
            }}
          >
            {!tableResponseData.length && (
              <Dropzone
                accept={{ "application/vnd.ms-excel": [] }}
                multiple={false}
                onDrop={(acceptedFiles) => {
                  const file = acceptedFiles?.[0];
                  if (file) {
                    setFile(file);
                  }
                }}
              >
                {({ getRootProps, getInputProps, isDragActive }) => (
                  <section
                    className="editor-drop-area"
                    style={{
                      backgroundColor: isDragActive ? "#eef7ff" : "#f9f9f9",
                    }}
                  >
                    <div {...getRootProps()} style={{ padding: "40px" }}>
                      <input {...getInputProps()} />
                      <p>{"Seleccionar o arrastrar archivo .xls"}</p>
                    </div>
                  </section>
                )}
              </Dropzone>
            )}

            {tableData.length > 0 && !tableResponseData.length && (
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontFamily: "Nunito" }} align="center">
                        Es válido
                      </TableCell>
                      <TableCell sx={{ fontFamily: "Nunito" }} align="center">
                        Trabajo
                      </TableCell>
                      <TableCell sx={{ fontFamily: "Nunito" }} align="center">
                        RPU
                      </TableCell>
                      <TableCell sx={{ fontFamily: "Nunito" }} align="center">
                        Proyecto
                      </TableCell>
                      <TableCell sx={{ fontFamily: "Nunito" }} align="center">
                        Fase
                      </TableCell>
                      <TableCell sx={{ fontFamily: "Nunito" }} align="center">
                        Instalador
                      </TableCell>
                      <TableCell sx={{ fontFamily: "Nunito" }} align="center">
                        Nombre del instalador
                      </TableCell>
                      <TableCell sx={{ fontFamily: "Nunito" }} align="left">
                        Empresa del instalador
                      </TableCell>
                      <TableCell sx={{ fontFamily: "Nunito" }} align="left">
                        Mensaje
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tableData.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ fontFamily: "Nunito" }} align="center">
                          {row?.esValida ? (
                            <CheckCircleOutlineIcon color="success" />
                          ) : (
                            <ErrorOutlineIcon color="error" />
                          )}
                        </TableCell>
                        <TableCell sx={{ fontFamily: "Nunito" }} align="left">
                          {row?.nombreTrabajo}
                        </TableCell>
                        <TableCell sx={{ fontFamily: "Nunito" }} align="left">
                          {row?.rpu}
                        </TableCell>
                        <TableCell sx={{ fontFamily: "Nunito" }} align="left">
                          {row?.nombreProyecto}
                        </TableCell>
                        <TableCell sx={{ fontFamily: "Nunito" }} align="left">
                          {row?.faseTrabajo}
                        </TableCell>
                        <TableCell sx={{ fontFamily: "Nunito" }} align="left">
                          {row?.credencial}
                        </TableCell>
                        <TableCell sx={{ fontFamily: "Nunito" }} align="left">
                          {row?.nombreInstalador}
                        </TableCell>
                        <TableCell sx={{ fontFamily: "Nunito" }} align="left">
                          {row?.razonSocial}
                        </TableCell>
                        <TableCell sx={{ fontFamily: "Nunito" }} align="left">
                          {row?.mensaje}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {tableResponseData.length > 0 && (
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontFamily: "Nunito" }} align="center">
                        {" "}
                      </TableCell>
                      <TableCell sx={{ fontFamily: "Nunito" }} align="left">
                        Descripción
                      </TableCell>
                      <TableCell sx={{ fontFamily: "Nunito" }} align="left">
                        Mensaje
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tableResponseData.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ fontFamily: "Nunito" }} align="center">
                          {row?.exitoso ? (
                            <CheckCircleOutlineIcon color="success" />
                          ) : (
                            <ErrorOutlineIcon color="error" />
                          )}
                        </TableCell>
                        <TableCell sx={{ fontFamily: "Nunito" }} align="left">
                          {row?.descripcion}
                        </TableCell>
                        <TableCell sx={{ fontFamily: "Nunito" }} align="left">
                          {row?.mensaje === "SAVEOK"
                            ? "Se guardó correctamente"
                            : row?.mensaje}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <CustomButton
            background={"#FFFFFF"}
            color={"#223D51"}
            text="Cerrar"
            height="40px"
            fontWeight="700"
            fontSize="14px"
            border="1px solid #D9D9D9"
            type="button"
            width={"100px"}
            onClick={handleClose}
          />
          {!tableResponseData.length && (
            <CustomButton
              background={"#e3e3e3"}
              color={"#223D51"}
              text="Limpiar no válidos"
              height="40px"
              fontWeight="700"
              fontSize="14px"
              border="1px solid #D9D9D9"
              type="button"
              width={"150px"}
              onClick={limpiarNoValidos}
            />
          )}
          {!tableResponseData.length && (
            <CustomButton
              background={"#223D51"}
              color={"#FFFFFF"}
              text="Aplicar"
              height="40px"
              fontWeight="700"
              fontSize="14px"
              type="button"
              width={"100px"}
              onClick={() => {
                const payload = tableData.filter(
                  (row) => row.esValida === true
                );
                if (payload.length === 0) {
                  setShowSnackbar(true);
                  setSnackbarMessage("No hay asignaciones válidas.");
                  return;
                } else {
                  setShowConfirm(true);
                }
              }}
            />
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ModalSubirExcelAsignaciones;
